import React, { useState, useEffect, useCallback } from 'react';
import { Book, ExternalLink, ShoppingCart, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '@/utils/api';

/**
 * BookRecommendations - Displays affiliate book recommendations in a carousel
 * Only shows for admin-created calls (not user suggestions)
 * Matches books based on call's knowledge_areas
 * Shows 3 books at a time with navigation arrows
 */
const BookRecommendations = ({ knowledgeAreas = [], isAdminCall = false }) => {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);

  const BOOKS_PER_PAGE = 3;

  useEffect(() => {
    // Only fetch books for admin-created calls
    if (!isAdminCall || !knowledgeAreas || knowledgeAreas.length === 0) {
      setLoading(false);
      return;
    }

    fetchBooks();
  }, [knowledgeAreas, isAdminCall]);

  const fetchBooks = async () => {
    try {
      const areasParam = knowledgeAreas.join(',');
      const response = await api.get(`/api/books/by-areas?areas=${encodeURIComponent(areasParam)}`);
      setBooks(response.data.books || []);
    } catch (error) {
      console.error('Failed to fetch books:', error);
    } finally {
      setLoading(false);
    }
  };

  const trackClick = async (bookId, platform) => {
    try {
      await api.post(`/api/books/${bookId}/click?platform=${platform}`);
    } catch (error) {
      // Silent fail - don't interrupt user experience
    }
  };

  const handleClick = (book, platform, link) => {
    trackClick(book.book_id, platform);
    window.open(link, '_blank', 'noopener,noreferrer');
  };

  // Carousel navigation
  const totalPages = Math.ceil(books.length / BOOKS_PER_PAGE);
  
  const nextSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % totalPages);
  }, [totalPages]);

  const prevSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + totalPages) % totalPages);
  }, [totalPages]);

  // Auto-advance carousel every 8 seconds
  useEffect(() => {
    if (books.length <= BOOKS_PER_PAGE) return;
    
    const interval = setInterval(nextSlide, 8000);
    return () => clearInterval(interval);
  }, [books.length, nextSlide]);

  // Get current visible books
  const visibleBooks = books.slice(
    currentIndex * BOOKS_PER_PAGE,
    currentIndex * BOOKS_PER_PAGE + BOOKS_PER_PAGE
  );

  // Don't render anything if not admin call or no books
  if (!isAdminCall || loading || books.length === 0) {
    return null;
  }

  return (
    <div className="mt-8 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
      {/* Header with navigation */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Book className="h-5 w-5 text-[#002FA7]" />
          <h3 className="font-semibold text-[#0A0A0A]">Leituras Recomendadas</h3>
          {books.length > BOOKS_PER_PAGE && (
            <span className="text-xs text-gray-500 ml-2">
              ({currentIndex + 1}/{totalPages})
            </span>
          )}
        </div>
        
        {/* Navigation arrows - only show if more than 3 books */}
        {books.length > BOOKS_PER_PAGE && (
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={prevSlide}
              className="h-8 w-8 rounded-full hover:bg-white/80"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={nextSlide}
              className="h-8 w-8 rounded-full hover:bg-white/80"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
      
      {/* Books carousel */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 transition-all duration-300">
        {visibleBooks.map((book) => (
          <div 
            key={book.book_id}
            className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow"
          >
            {/* Book Cover */}
            <div className="flex gap-3 p-3">
              <div className="flex-shrink-0">
                <img
                  src={book.cover_url}
                  alt={book.title}
                  className="w-16 h-24 object-cover rounded shadow-sm"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/64x96?text=📚';
                  }}
                />
              </div>
              
              {/* Book Info */}
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-sm line-clamp-2 text-gray-900">
                  {book.title}
                </h4>
                <p className="text-xs text-gray-500 mt-1 line-clamp-1">
                  {book.author}
                </p>
              </div>
            </div>
            
            {/* Buy Buttons */}
            <div className="flex border-t border-gray-100">
              {book.amazon_link && (
                <button
                  onClick={() => handleClick(book, 'amazon', book.amazon_link)}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium text-orange-700 bg-orange-50 hover:bg-orange-100 transition-colors"
                >
                  <ShoppingCart className="h-3.5 w-3.5" />
                  Amazon
                  <ExternalLink className="h-3 w-3" />
                </button>
              )}
              {book.mercado_livre_link && (
                <button
                  onClick={() => handleClick(book, 'mercado_livre', book.mercado_livre_link)}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium text-yellow-700 bg-yellow-50 hover:bg-yellow-100 transition-colors ${book.amazon_link ? 'border-l border-gray-100' : ''}`}
                >
                  <ShoppingCart className="h-3.5 w-3.5" />
                  Mercado Livre
                  <ExternalLink className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
      
      {/* Dots indicator - only show if more than 3 books */}
      {books.length > BOOKS_PER_PAGE && (
        <div className="flex justify-center gap-2 mt-4">
          {Array.from({ length: totalPages }).map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`h-2 rounded-full transition-all ${
                index === currentIndex 
                  ? 'w-6 bg-[#002FA7]' 
                  : 'w-2 bg-gray-300 hover:bg-gray-400'
              }`}
            />
          ))}
        </div>
      )}
      
      <p className="text-xs text-gray-500 mt-4 text-center">
        Links de afiliado - Ao comprar, você apoia o Quero Publicar
      </p>
    </div>
  );
};

export default BookRecommendations;
